import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '../primitives/button.js'
import { conflictLead, conflictNoticeCopy as copy } from './conflict-notice-copy.js'
import styles from './conflict-banner.module.css'

export interface ConflictBannerProps {
  /** The held fields this banner is about */
  fields: readonly string[]
  /** "Keep my changes": resolves once saved; a rejection shows the failure with "Try again" */
  onKeep: () => Promise<void>
  /** "Use their version" */
  onTakeTheirs: () => void
  /**
   * Take focus: the banner, when Review opened its panel; "Keep my changes", in the Review dialog. By default it
   * takes none, so typing in the panel is not interrupted (spec, Accessibility).
   */
  focus?: 'region' | 'keep'
  /**
   * Name the region "Editing conflict". Off in the Review dialog, whose title already says it, so it is not announced
   * twice (GH #2713). @default true
   */
  labelled?: boolean
}

/**
 * Someone else changed a list or setting I was editing (GH #1912 option B, docs/ux/2026-10-24-conflict-notice-ux.md).
 * Inline, at the top of the panel that edits the fields. It stays until the user acts: no timeout (WCAG 2.2.1) and no
 * close button, since closing would leave the edit in limbo. It takes no focus on mount unless asked to: the lead
 * line is announced as an alert, and the buttons are next in the tab order.
 */
export const ConflictBanner: FC<ConflictBannerProps> = props => {
  const { fields, onKeep, onTakeTheirs, focus, labelled = true } = props
  const [state, setState] = useState<'idle' | 'saving' | 'failed'>('idle')
  const lead = conflictLead(fields)
  const regionRef = useRef<HTMLElement>(null)
  const keepRef = useRef<HTMLButtonElement>(null)
  const tryAgainRef = useRef<HTMLButtonElement>(null)
  /** Keep had focus when its save failed; Try again, which replaces it, takes it (GH #2713) */
  const [moveFocusToRetry, setMoveFocusToRetry] = useState(false)

  useEffect(() => {
    if (state === 'failed' && moveFocusToRetry) {
      tryAgainRef.current?.focus()
      setMoveFocusToRetry(false)
    }
  }, [state, moveFocusToRetry])

  useEffect(() => {
    if (focus === 'region') {
      regionRef.current?.focus()
    }
    if (focus === 'keep') {
      keepRef.current?.focus()
    }
  }, [focus])

  const handleKeep = async () => {
    const keepHadFocus = document.activeElement === keepRef.current
    setState('saving')
    try {
      await onKeep()
      setState('idle')
    } catch {
      setMoveFocusToRetry(keepHadFocus)
      setState('failed')
    }
  }

  return (
    <section ref={regionRef} tabIndex={-1} className={styles.root} aria-label={labelled ? copy.region : undefined}>
      <div role="alert">
        {lead.kind === 'one' ? (
          <p className={styles.lead}>{lead.text}</p>
        ) : (
          <>
            <p className={styles.lead}>{lead.lead}</p>
            <ul className={styles.list}>
              {lead.items.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            {lead.more ? <p className={styles.lead}>{lead.more}</p> : null}
          </>
        )}
      </div>
      <p className={styles.subline}>{copy.subline}</p>
      {state === 'failed' ? (
        <p className={styles.failed}>
          {copy.keepFailed}
          <Button ref={tryAgainRef} size="small" color="error" onClick={() => void handleKeep()}>
            {copy.tryAgain}
          </Button>
        </p>
      ) : null}
      <div className={styles.actions}>
        {/* GH #2713: while a save has failed, Try again stands in for Keep */}
        {state === 'failed' ? null : (
          <Button ref={keepRef} variant="contained" disabled={state === 'saving'} onClick={() => void handleKeep()}>
            {copy.keep}
          </Button>
        )}
        <Button variant="outlined" disabled={state === 'saving'} onClick={onTakeTheirs}>
          {copy.takeTheirs}
        </Button>
      </div>
    </section>
  )
}

export default ConflictBanner
